import React, { useEffect, useState } from "react";
import { Select, MenuItem } from "@mui/material";
import CustomBreadCrumbs from "../BreadCrumb/BreadCrumb";
import MyTable from "../../MyTable";
import Chip from "@mui/material/Chip";
import "./UserManagement.css";
// import SettingsIcon from "@mui/icons-material/Settings"; // not used
// settings asset removed as action column is replaced by status dropdown
import uploadPlus from "../../assets/upload-plus.png";
import apiService from "../../service";
import ToastMessage from "../Common/ReactToastifyNotification/Toastify";
import Loader from "../Common/Loader";
import UserModal from "./UserModal";
import { Grid, Typography } from "@mui/material";
import NodataImage from "../../assets/NoData.png";

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalInitialData, setModalInitialData] = useState({});
  const role = localStorage.getItem("role");
  const isAdmin = role && String(role).toLowerCase() === "admin";
  // Fetch users
  const fetchUsers = async () => {
    setOpen(true);
    try {
      const response = await apiService.getTenantList();
      // If response is { status_code, message, data: [...] }
      const userList = Array.isArray(response?.data) ? response.data : [];
      setUsers(userList);
    } catch (err) {
      // failed to fetch users
      setUsers([]);
    } finally {
      setOpen(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleInviteUser = () => {
    setModalInitialData({});
    setModalOpen(true);
  };

  const handleModalSubmit = async (form) => {
    setModalLoading(true);
    try {
      await apiService.inviteTenantUser(form);
      return;
    } catch (err) {
      setModalOpen(false);
    } finally {
      fetchUsers();
      setModalOpen(false);
      setModalLoading(false);
    }
  };

  const handleStatusChange = async (userId, selectedValue) => {
    // selectedValue expected to be 'Active' or 'Inactive'
    const isActivate = selectedValue.toLowerCase() === "active";
    const statusParam = isActivate ? "activate" : "deactivate";
    try {
      const resp = await apiService.deactivateTenantUser(userId, statusParam);
      ToastMessage.success(
        resp?.message ||
          `User ${isActivate ? "activated" : "deactivated"} successfully`
      );
      await fetchUsers();
    } catch (err) {
      const msg = err?.message || "Failed to update status";
      ToastMessage.error(msg);
    }
  };

  const records = users.map((user, idx) => ({
    id: user._id || idx,
    name: user.name,
    email: user.email,
    role: user.role,
    lastLogin: user.updated_at
      ? new Date(user.updated_at).toISOString().split("T")[0]
      : "",
    status:
      user.status.charAt(0).toUpperCase() + user.status.slice(1).toLowerCase(),
    boundary: {},
    extracted: "",
  }));

  const statusStyles = {
    Active: { bg: "#E6F4EA", color: "rgb(25, 173, 25)", border: 0 },
    Pending: { bg: "#FFF4E5", color: "#ED6C02", border: 0 },
    Invited: { bg: "#E3F2FD", color: "#1976D2", border: 0 },
    Deactivate: { bg: "#F2F2F2", color: "#B71C1C", border: 0 },
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 2 },
    { field: "email", headerName: "Email", flex: 3 },
    { field: "role", headerName: "Role", flex: 2 },
    { field: "lastLogin", headerName: "Last Logged In", flex: 2 },
    {
      field: "status",
      headerName: "Status",
      flex: 2,
      renderCell: (params) => {
        const style = statusStyles[params.value] || {
          bg: "#F2F2F2",
          color: "#333",
          border: 0,
        };
        const userId = params.row.id;
        const currentValue = params.value;
        const raw = String(currentValue || "").toLowerCase();
        const isToggleable = isAdmin && (
          raw === "active" || raw === "deactivate" || raw === "inactive"
        );
        // If not toggleable, just show the chip
        if (!isToggleable) {
          return (
            <Chip
              label={currentValue}
              variant="outlined"
              size="small"
              sx={{
                backgroundColor: style.bg,
                color: style.color,
                border: 0,
                width: "130px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                padding: "6px 12px",
                fontFamily: "Inter",
                fontWeight: 500,
                fontSize: "13px",
                borderRadius: "16px",
              }}
            />
          );
        }

        return (
          <div className="status-select-wrapper">
            <Select
              value={currentValue}
              size="small"
              variant="standard"
              onChange={(e) => handleStatusChange(userId, e.target.value)}
              renderValue={(val) => (
                <Chip
                  label={val}
                  variant="outlined"
                  size="small"
                  sx={{
                    backgroundColor: style.bg,
                    color: style.color,
                    border: 0,
                    width: "130px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    padding:
                      "6px 36px 6px 12px" /* leave space for the icon inside */,
                    fontFamily: "Inter",
                    fontWeight: 500,
                    fontSize: "13px",
                    borderRadius: "16px",
                  }}
                />
              )}
              sx={{
                minWidth: 130,
                backgroundColor: "transparent",
                // ensure the select wrapper doesn't add an outlined box
                "& .MuiSelect-select": {
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  background: "transparent",
                },
                "& .MuiSelect-icon": {
                  right: 10,
                  color: style.color,
                  background: "transparent",
                },
                // remove any root input background or border
                "& .MuiInputBase-root": {
                  background: "transparent",
                  boxShadow: "none",
                },
                "& .Mui-focused .MuiSelect-select": {
                  background: "transparent",
                },
              }}
            >
              <MenuItem value={"Active"}>Active</MenuItem>
              <MenuItem value={"Deactivated"}>Deactivated</MenuItem>
            </Select>
          </div>
        );
      },
    },
  ];

  return (
    <div className="page-container">
      <div className="user-management-header">
        {open && <Loader open={open} handleClose={() => setOpen(false)} />}
        <CustomBreadCrumbs
          array={[
            {
              name: "User Management",
              color: "#767676",
              underline: "none",
            },
          ]}
        />
        <div className="button-container">
          <div className="button-container-end">
            {isAdmin && (
              <button className="analyse-upload" onClick={handleInviteUser}>
                <img
                  style={{ marginRight: 5 }}
                  src={uploadPlus}
                  alt="Invite"
                  height={15}
                />{" "}
                Invite User
              </button>
            )}
          </div>
        </div>
      </div>
      {!open && records.length > 0 && (
        <MyTable records={records} columns={columns} />
      )}
      {!open && records.length === 0 && (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: "60vh" }}
        >
          <Grid item style={{ textAlign: "center" }}>
            <img src={NodataImage} alt="No Data" className="nodataImage" />
            <Typography className="nodata-title">No Data Available</Typography>

            <div className="button-data">
              {isAdmin && (
                <button className="analyse-upload" onClick={handleInviteUser}>
                  <img
                    style={{ marginRight: 5 }}
                    src={uploadPlus}
                    alt="Invite"
                    height={15}
                  />{" "}
                  Invite User
                </button>
              )}
            </div>
          </Grid>
        </Grid>
      )}
      {/* Confirmation dialog removed; status dropdown handles activate/deactivate */}
      <UserModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
        initialData={modalInitialData}
        loading={modalLoading}
      />
    </div>
  );
}

export default UserManagement;
